<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="orderTableData"
      :columns="orderTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :compact-pagination="true"
      :enable-column-visibility="false"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: [],
          right: [],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-global-filter="currentSavedState?.globalFilter || ''"
      :initial-sorting="currentSavedState?.sorting || [{ id: 'order_number', desc: true }]"
      :initial-page-size="currentSavedState?.pageSize || 10"
      enable-row-selection
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      :initial-row-selection="initialRowSelection"
      :get-row-id="row => row.id.toString()"
    >
      <template #config-manager>
        <div class="tw-flex tw-flex-col tw-gap-2 tw-text-sm tw-font-bold tw-text-secondary-600">
          {{ t('orders.titles.selected_orders') }}
        </div>
      </template>
    </FancyTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrderStore } from '@/stores/order.store';
import {
  FancyTable,
  type FancyTableColumnDef,
  useTableRenderers,
  FieldType,
  FilterType,
} from '@/components/shared/fancyTables';

// import OrdersTableRowActions from "./OrdersTableRowActions.vue";
import ServiceDateCell from './ServiceDateCell.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';
import type { RowSelectionState } from '@tanstack/vue-table';

const fancyTableRef = ref<any>(null);
const { GetFieldRenderer } = useTableRenderers();

const { getOrders } = useOrderStore();
const { t } = useI18n();

const initialRowSelection = ref<RowSelectionState>({});

const props = defineProps({
  customerId: {
    default: null,
    type: Number,
  },
  orders: {
    default: () => [],
    type: Array,
  },
});

const getOrdersTableList = async () => {
  isLoading.value = true;
  const response = await getOrders({
    page: 1,
    page_size: -1,
    customer: props.customerId,
    real_status: 'APPROVED',
    is_active: true,
  });

  // Force complete object recreation to ensure TanStack Table detects changes
  // This ensures each row object has a new reference so TanStack Table can detect updates
  orderTableData.value = response.results.map(item => ({
    ...item,
    ['temp_amount']: 0,
  }));
  isLoading.value = false;
};

const emit = defineEmits(['order-selected']);

// Initialize the table config composable
const { currentSavedState, debouncedHandleTableStateChange, isStateLoaded } = useFancyTableConfig({
  tableName: 'selected_orders',
  fancyTableRef,
  tableLoader: getOrdersTableList,
});

const isLoading = ref(false);
const orderTableData = ref<any[]>([]);

const orderTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('orders.titles.order_no'),
    accessorKey: 'order_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.supplier'),
    accessorKey: 'supplier_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.doc_no_internal'),
    enableSorting: true,
    accessorKey: 'document_no_internal',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 250,
  },
  {
    header: t('orders.titles.service_date'),
    accessorKey: 'service_date',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: info =>
      h(ServiceDateCell, {
        key: `service-date-${info.row.original.id}-${info.row.original.service_date}-${info.row.original.new_service_date}`,
        order: info.row.original,
        onOrderUpdated: handleOrderUpdate,
      }),
    filterFn: 'dateRange',
    size: 200,
  },
  {
    header: t('orders.titles.amount_gross'),
    accessorKey: 'amount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    size: 150,
  },
  {
    header: t('orders.titles.remaining_balance'),
    accessorKey: 'balance',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    size: 150,
  },
  {
    header: t('orders.titles.released_by'),
    accessorKey: 'released_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  emit('order-selected', selection);
};

const handleOrderUpdate = (orderId: number, updatedOrder: any) => {
  // Find and update the order in the table data
  const orderIndex = orderTableData.value.findIndex((order: any) => order.id === orderId);
  if (orderIndex !== -1) {
    orderTableData.value[orderIndex] = {
      ...orderTableData.value[orderIndex],
      ...updatedOrder,
    };
  }
};

onMounted(async () => {
  // Set initial selection before loading data
  if (Array.isArray(props.orders) && props.orders.length > 0) {
    initialRowSelection.value = props.orders.reduce((acc: Record<string, boolean>, order: any) => {
      if (order && typeof order.order_id !== 'undefined') {
        acc[order.order_id.toString()] = true;
      }
      return acc;
    }, {});
  }

  await getOrdersTableList();
  const selectedIds = props.orders.map((order: any) => order.order_id);
  const selectedOrders = orderTableData.value.filter((order: any) =>
    selectedIds.includes(order.id)
  );
  handleRowSelectionChange(selectedOrders);
});
</script>
